<template>
  <v-dialog
    :model-value="modelValue"
    max-width="600"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="help-dialog">
      <v-card-text class="pa-6">
        <div class="d-flex flex-column align-center mb-6">
          <v-avatar
            size="120"
            class="bg-primary mb-4 elevation-2"
            color="primary"
          >
            <v-icon
              size="64"
              color="grey-lighten-3"
            >
              mdi-help-circle
            </v-icon>
          </v-avatar>
          <h2 class="text-h5 font-weight-medium mb-1">
            Help Center
          </h2>
          <p class="text-subtitle-1 text-medium-emphasis">
            Find answers and support
          </p>
        </div>

        <v-divider class="mb-6" />

        <v-expansion-panels
          variant="accordion"
          class="help-panels"
        >
          <!-- 
          <v-expansion-panel elevation="0">
            <v-expansion-panel-title class="text-subtitle-1 bg-surface">
              <template #default="{ expanded }">
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    class="d-flex align-center"
                  >
                    <v-icon
                      :icon="expanded ? 'mdi-book-open-variant' : 'mdi-book-open-outline'"
                      class="me-2"
                      color="primary"
                    />
                    Knowledge Base
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="bg-surface">
              <p class="mb-4">
                Access our comprehensive documentation for detailed guides and references.
              </p>
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-open-in-new"
                href="https://docs.example.com"
                target="_blank"
              >
                View Documentation
              </v-btn>
            </v-expansion-panel-text>
          </v-expansion-panel> 
          -->

          <v-expansion-panel elevation="0">
            <v-expansion-panel-title class="text-subtitle-1 bg-surface">
              <template #default="{ expanded }">
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    class="d-flex align-center"
                  >
                    <v-icon
                      :icon="expanded ? 'mdi-frequently-asked-questions' : 'mdi-help-circle-outline'"
                      class="me-2"
                      color="primary"
                    />
                    FAQs
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="bg-surface">
              <div class="mb-4">
                <h3 class="text-subtitle-1 font-weight-bold mb-2">
                  Common Questions
                </h3>
                <v-list>
                  <v-list-item
                    v-for="(faq, index) in faqs"
                    :key="index"
                    :title="faq.question"
                    :subtitle="faq.answer"
                  />
                </v-list>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel elevation="0">
            <v-expansion-panel-title class="text-subtitle-1 bg-surface">
              <template #default="{ expanded }">
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    class="d-flex align-center"
                  >
                    <v-icon
                      :icon="expanded ? 'mdi-email' : 'mdi-email-outline'"
                      class="me-2"
                      color="primary"
                    />
                    Contact Support
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="bg-surface">
              <p class="mb-4">
                Need additional help? Our support team is here for you.
              </p>
              <v-btn
                color="primary"
                prepend-icon="mdi-email"
                :href="`mailto:${brand.supportEmail}`"
              >
                Email Support
              </v-btn>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Legal Documents Section -->
          <v-expansion-panel elevation="0">
            <v-expansion-panel-title class="text-subtitle-1 bg-surface">
              <template #default="{ expanded }">
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    class="d-flex align-center"
                  >
                    <v-icon
                      :icon="expanded ? 'mdi-file-document' : 'mdi-file-document-outline'"
                      class="me-2"
                      color="primary"
                    />
                    Legal Agreements
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="bg-surface">
              <p class="mb-4">
                Status of your organization's legal agreements with Peraso.
              </p>
              <v-list class="pa-0">
                <v-list-item
                  v-for="doc in signableDocuments"
                  :key="doc.id"
                  class="px-0"
                >
                  <template #prepend>
                    <v-icon
                      :icon="doc.is_signed ? 'mdi-check-circle-outline' : 'mdi-file-document-edit-outline'"
                      :color="doc.is_signed ? 'success' : 'primary'"
                      class="me-2"
                    />
                  </template>

                  <v-list-item-title>
                    {{ doc.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ doc.is_signed ? 'Signed' : 'Waiting for signature' }}
                  </v-list-item-subtitle>
                </v-list-item>

                <v-list-item
                  v-if="!signableDocuments.length"
                  class="px-0"
                >
                  <v-list-item-subtitle class="text-medium-emphasis">
                    No documents found
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          variant="text"
          @click="emit('update:modelValue', false)"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'

import { brand } from '@/config/brand'
import { useLegalDocumentStore } from '@/stores/legal_document'
import type { ISignableDocument } from '@/models/legal_documents'

interface Props {
  modelValue: boolean
}

defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const faqs = [
  {
    question: 'How do I reset my password?',
    answer: 'Click "Forget Password" on the login page to reset your password.'
  },
  {
    question: 'Where are downloaded files saved?',
    answer: 'Files are typically saved to your Downloads folder.'
  },
  {
    question: 'Where can I find organization signed legal agreements?',
    answer: 'After signing, a copy of the agreement is sent to the signing authority.'
  }
]

const legalDocumentStore = useLegalDocumentStore()
const isLoadingDocuments = ref(false)

const signableDocuments = computed<ISignableDocument[]>(() => {
  return legalDocumentStore.getSignableDocuments
})

onMounted(async () => {
  try {
    isLoadingDocuments.value = true
    await legalDocumentStore.fetchSignableDocuments()
  } catch (error) {
    console.error('Failed to fetch documents:', error)
  } finally {
    isLoadingDocuments.value = false
  }
})
</script>

<style scoped>
.help-dialog :deep(.v-expansion-panel) {
  background: transparent;
}

.help-dialog :deep(.v-expansion-panel-title) {
  padding: 16px;
  border-radius: 8px;
}

.help-dialog :deep(.v-expansion-panel-text__wrapper) {
  padding: 20px;
}

.help-dialog :deep(.v-expansion-panel--active > .v-expansion-panel-title) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.help-panels {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}
</style> 